import { analytics } from "~/analytics";
import { observeAudioForE2e } from "./audioE2eProbe.client";
import { playerEngine } from "./playerEngine";

export interface AudioTimingMetrics {
  src: string;
  ttfb: number;
  startTime: number;
  requestStart: number;
  responseStart: number;
  responseEnd: number;
  transferSize: number;
  encodedBodySize: number;
  decodedBodySize: number;
  timing: {
    dns: number;
    connect: number;
    ssl: number;
    ttfb: number;
    download: number;
    total: number;
  };
}

export type AudioLoadOutcome = "playable" | "element_error" | "metadata_timeout" | "aborted";

export interface AudioTelemetryContext {
  sequenceId?: string;
  trackIndex?: number;
  totalTracks?: number;
}

export interface AudioLoadOutcomeDetails {
  errorCode?: number;
  errorMessage?: string;
  timeoutMs?: number;
}

export interface InstrumentedAudio extends HTMLAudioElement {
  getMetrics(): AudioTimingMetrics | null;
  hasMeasured(): boolean;
  markLoadOutcome(outcome: AudioLoadOutcome, details?: AudioLoadOutcomeDetails): boolean;
  destroy(): void;
}

const MAX_RETRIES = 10;
const AUDIO_LOAD_ATTEMPTED_EVENT = "Episode Audio Load Attempted";
const AUDIO_LOAD_RESULT_EVENT = "Episode Audio Load Result";
const AUDIO_LOAD_PERFORMANCE_EVENT = "Episode Audio Load Performance";
let loadAttemptCounter = 0;

function safeDuration(start: number, end: number): number {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.max(0, end - start);
}

function nowMs(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function extractAudioHost(source?: string) {
  if (!source) return undefined;
  try {
    return new URL(source).hostname;
  } catch {
    return "invalid-url";
  }
}

function findResourceEntry(src: string): PerformanceResourceTiming | null {
  if (!src || typeof performance === "undefined" || typeof performance.getEntriesByName !== "function") {
    return null;
  }

  const entries = performance.getEntriesByName(src, "resource") as Array<PerformanceResourceTiming>;
  if (!entries.length) return null;

  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const candidate = entries[index];
    if (candidate?.responseStart > 0 && candidate.requestStart > 0) {
      return candidate;
    }
  }

  return null;
}

function cloneMetrics(metrics: AudioTimingMetrics | null): AudioTimingMetrics | null {
  if (!metrics) return null;
  return { ...metrics, timing: { ...metrics.timing } };
}

function createLoadAttemptId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  loadAttemptCounter += 1;
  return `audio-load-${Date.now().toString(36)}-${loadAttemptCounter}`;
}

function getBrowser() {
  return typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
}

function buildTelemetryProperties(
  loadAttemptId: string,
  source: string,
  context: AudioTelemetryContext,
): Record<string, unknown> {
  return {
    load_attempt_id: loadAttemptId,
    sequence_id: context.sequenceId,
    track_index: context.trackIndex,
    total_tracks: context.totalTracks,
    audio_source_url: source,
    audio_host: extractAudioHost(source),
    browser: getBrowser(),
  };
}

export function createInstrumentedAudio(src?: string, telemetryContext: AudioTelemetryContext = {}): InstrumentedAudio {
  const audio = new Audio();
  observeAudioForE2e(audio);

  let destroyed = false;
  let measured = false;
  let measuredFromFallback = false;
  let metrics: AudioTimingMetrics | null = null;
  let pendingSrc = src ?? "";
  let pendingSrcStartMs = nowMs();
  let trackedForCurrentSrc = false;
  let currentAttempt: { id: string; src: string; startedAtMs: number; settled: boolean } | null = null;
  let rafId: number | null = null;

  const cancelPendingRaf = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const startLoadAttempt = (nextSrc: string) => {
    if (!nextSrc) return;

    const startedAtMs = nowMs();
    const id = createLoadAttemptId();
    currentAttempt = {
      id,
      src: nextSrc,
      startedAtMs,
      settled: false,
    };

    analytics.track({
      event: AUDIO_LOAD_ATTEMPTED_EVENT,
      properties: buildTelemetryProperties(id, nextSrc, telemetryContext),
    });
  };

  const settleLoadAttempt = (outcome: AudioLoadOutcome, details: AudioLoadOutcomeDetails = {}) => {
    if (!currentAttempt || currentAttempt.settled) return false;

    currentAttempt.settled = true;
    const elapsedMs = Math.max(0, nowMs() - currentAttempt.startedAtMs);

    analytics.track({
      event: AUDIO_LOAD_RESULT_EVENT,
      properties: {
        ...buildTelemetryProperties(currentAttempt.id, currentAttempt.src, telemetryContext),
        outcome,
        elapsed_ms: elapsedMs,
        error_code: details.errorCode,
        error_message: details.errorMessage,
        timeout_ms: details.timeoutMs,
      },
    });

    return true;
  };

  const reportMetrics = (entry: PerformanceResourceTiming, currentSrc: string) => {
    const ttfb = safeDuration(entry.requestStart, entry.responseStart);

    metrics = {
      src: currentSrc,
      ttfb,
      startTime: entry.startTime,
      requestStart: entry.requestStart,
      responseStart: entry.responseStart,
      responseEnd: entry.responseEnd,
      transferSize: entry.transferSize,
      encodedBodySize: entry.encodedBodySize,
      decodedBodySize: entry.decodedBodySize,
      timing: {
        dns: safeDuration(entry.domainLookupStart, entry.domainLookupEnd),
        connect: safeDuration(entry.connectStart, entry.connectEnd),
        ssl: entry.secureConnectionStart > 0 ? safeDuration(entry.secureConnectionStart, entry.connectEnd) : 0,
        ttfb,
        download: safeDuration(entry.responseStart, entry.responseEnd),
        total: entry.duration > 0 ? entry.duration : safeDuration(entry.startTime, entry.responseEnd),
      },
    };
    measured = true;
    measuredFromFallback = false;
  };

  const reportFallbackMetrics = (currentSrc: string) => {
    const elapsed = Math.max(0, nowMs() - pendingSrcStartMs);
    metrics = {
      src: currentSrc,
      ttfb: elapsed,
      startTime: 0,
      requestStart: 0,
      responseStart: 0,
      responseEnd: 0,
      transferSize: 0,
      encodedBodySize: 0,
      decodedBodySize: 0,
      timing: {
        dns: 0,
        connect: 0,
        ssl: 0,
        ttfb: elapsed,
        download: 0,
        total: elapsed,
      },
    };
    measured = true;
    measuredFromFallback = true;
  };

  const trackAudioLoadPerformance = (timeToPlayableMs: number) => {
    if (trackedForCurrentSrc || !pendingSrc || !metrics || !currentAttempt) return;

    analytics.track({
      event: AUDIO_LOAD_PERFORMANCE_EVENT,
      properties: {
        ...buildTelemetryProperties(currentAttempt.id, pendingSrc, telemetryContext),
        time_to_first_byte_ms: metrics.ttfb,
        time_to_playable_ms: timeToPlayableMs,
        measurement_source: measuredFromFallback ? "event_fallback" : "resource_timing",
        startTime: metrics.startTime,
        requestStart: metrics.requestStart,
        responseStart: metrics.responseStart,
        responseEnd: metrics.responseEnd,
        transferSize: metrics.transferSize,
        encodedBodySize: metrics.encodedBodySize,
        decodedBodySize: metrics.decodedBodySize,
        timing: metrics.timing,
      },
    });

    trackedForCurrentSrc = true;
  };

  const tryMeasure = (attempt = 0) => {
    if (destroyed || !pendingSrc) return;
    if (measured && !measuredFromFallback) return;

    const entry = findResourceEntry(pendingSrc);
    if (entry) {
      reportMetrics(entry, pendingSrc);
      return;
    }

    if (attempt >= MAX_RETRIES) {
      return;
    }

    cancelPendingRaf();
    rafId = requestAnimationFrame(() => {
      rafId = null;
      tryMeasure(attempt + 1);
    });
  };

  const onLoadedMetadata = () => {
    const timeToPlayableMs = Math.max(0, nowMs() - pendingSrcStartMs);
    if (!measured && pendingSrc) {
      reportFallbackMetrics(pendingSrc);
    }
    tryMeasure();
    if (settleLoadAttempt("playable")) {
      trackAudioLoadPerformance(timeToPlayableMs);
    }
  };

  const onCanPlayThrough = () => {
    tryMeasure();
  };

  const onLoadedData = () => {
    // ResourceTiming can be unavailable for cross-origin audio without TAO.
    // Fallback to event-based first-byte approximation so ttfb is still measurable.
    if (!measured && pendingSrc) {
      reportFallbackMetrics(pendingSrc);
    }
    tryMeasure();
  };

  const onProgress = () => {
    if (measured || !pendingSrc) return;
    const hasBufferedData = audio.buffered.length > 0;
    if (hasBufferedData) {
      reportFallbackMetrics(pendingSrc);
      tryMeasure();
    }
  };

  audio.addEventListener("loadedmetadata", onLoadedMetadata);
  audio.addEventListener("canplaythrough", onCanPlayThrough);
  audio.addEventListener("loadeddata", onLoadedData);
  audio.addEventListener("progress", onProgress);

  if (src) {
    audio.src = src;
    startLoadAttempt(src);
  }

  const instrumentedAudio = audio as InstrumentedAudio;

  instrumentedAudio.getMetrics = () => cloneMetrics(metrics);
  instrumentedAudio.hasMeasured = () => measured;
  instrumentedAudio.markLoadOutcome = settleLoadAttempt;
  instrumentedAudio.destroy = () => {
    if (destroyed) return;
    destroyed = true;

    settleLoadAttempt("aborted");
    cancelPendingRaf();
    audio.removeEventListener("loadedmetadata", onLoadedMetadata);
    audio.removeEventListener("canplaythrough", onCanPlayThrough);
    audio.removeEventListener("loadeddata", onLoadedData);
    audio.removeEventListener("progress", onProgress);

    metrics = null;
    pendingSrc = "";
    pendingSrcStartMs = 0;
    trackedForCurrentSrc = false;
    currentAttempt = null;
    rafId = null;
  };

  return instrumentedAudio;
}

export function exitMeditationPlayback(): void {
  playerEngine.pause();
  playerEngine.reset();
}
